import { formatTime } from "../lib/format";
import type { Contact, IcpScoreEntry } from "../lib/types";
import { BreakdownGrid } from "./BreakdownGrid";
import { Pagination } from "./Pagination";

interface Props {
  contacts: Contact[];
  totalCount: number;
  page: number;
  pageSize: number;
  totalPages: number;
  onPageChange: (page: number) => void;
  selectedIds: Set<string>;
  onToggleSelect: (id: string) => void;
}

export function ContactList({
  contacts,
  totalCount,
  page,
  pageSize,
  totalPages,
  onPageChange,
  selectedIds,
  onToggleSelect,
}: Props) {
  if (totalCount === 0) {
    return (
      <div className="result">
        <p className="empty">No contacts match.</p>
      </div>
    );
  }

  const start = (page - 1) * pageSize + 1;
  const end = Math.min(page * pageSize, totalCount);

  return (
    <div className="result">
      {contacts.map((c) => (
        <ContactCard
          key={c.id}
          c={c}
          selected={selectedIds.has(c.id)}
          onToggleSelect={onToggleSelect}
        />
      ))}
      {totalPages > 1 && (
        <>
          <p
            className="card-desc"
            style={{ textAlign: "center", marginTop: 12, marginBottom: 0 }}
          >
            {start}–{end} of {totalCount}
          </p>
          <Pagination
            page={page}
            totalPages={totalPages}
            onChange={onPageChange}
          />
        </>
      )}
    </div>
  );
}

interface ContactCardProps {
  c: Contact;
  selected: boolean;
  onToggleSelect: (id: string) => void;
}

function ContactCard({ c, selected, onToggleSelect }: ContactCardProps) {
  const meta = [c.title, c.industry, c.country].filter(Boolean).join(" · ");
  const icpEntries = Object.entries(c.icp_scores);

  return (
    <div className={"contact" + (selected ? " contact-selected" : "")}>
      <div className="contact-top">
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <input
            type="checkbox"
            checked={selected}
            onChange={() => onToggleSelect(c.id)}
            aria-label={`Select ${c.name || c.email || c.id}`}
          />
          <div>
            <span className="contact-name">{c.name || "(no name)"}</span>
            {" — "}
            <span className="contact-company">
              {c.company || "(no company)"}
            </span>
          </div>
        </div>
        {icpEntries.length > 0 && (
          <div>
            {icpEntries.map(([icp, val]) => (
              <span key={icp} className="score" style={{ marginLeft: 6 }}>
                {icp}: {val.score ?? "—"}
                {val.pushed ? " ✓" : ""}
              </span>
            ))}
          </div>
        )}
      </div>
      {meta && <div className="contact-company">{meta}</div>}
      {c.email && <div className="contact-email">{c.email}</div>}
      {c.domain && <div className="contact-email">{c.domain}</div>}
      {c.created_at && (
        <div className="icp-entry-meta">
          Created {formatTime(c.created_at)}
        </div>
      )}

      {icpEntries.map(([icp, entry]) => (
        <IcpEntryDetail key={icp} icp={icp} entry={entry} />
      ))}
    </div>
  );
}

interface IcpEntryDetailProps {
  icp: string;
  entry: IcpScoreEntry;
}

function IcpEntryDetail({ icp, entry }: IcpEntryDetailProps) {
  const status = describeStatus(entry);
  return (
    <div className="icp-entry">
      <div className="icp-entry-header">
        <span className="icp-entry-name">{icp}</span>
        <span className="icp-entry-status">{status}</span>
      </div>

      <BreakdownGrid breakdown={entry.breakdown} />

      {entry.rationale && (
        <>
          <div className="rationale-label">Rationale</div>
          <div className="rationale">{entry.rationale}</div>
        </>
      )}

      {entry.processed_at && (
        <div className="icp-entry-meta">
          Processed {formatTime(entry.processed_at)}
        </div>
      )}
    </div>
  );
}

function describeStatus(entry: IcpScoreEntry): string {
  if (entry.excluded_by) {
    return `Excluded by ${entry.excluded_by}`;
  }
  const score = entry.score != null ? `${entry.score}/100` : "no score";
  return entry.pushed
    ? `Pushed to Lemlist · ${score}`
    : `Not pushed · ${score}`;
}

